// src/components/admin/SaleModal.tsx

import React, { useState } from 'react';
import { X, Percent, DollarSign, Info } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
import PriceInput from '../common/PriceInput';
import { formatDateTimeEnglish } from '../../utils/date';

interface SaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void;
  productName: string;
  productImage?: string;
  basePrice: number;
  variantId?: number | null;
  isVariant?: boolean;
}

export const SaleModal: React.FC<SaleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  productName,
  productImage,
  basePrice,
  variantId,
  isVariant,
}) => {
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<number>(10);
  const now = new Date();
  const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  const [startDate, setStartDate] = useState<string>(() => {
    return localNow.toISOString().slice(0, 16);
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const future = new Date(localNow);
    future.setDate(future.getDate() + 7);
    return future.toISOString().slice(0, 16);
  });

  const [errors, setErrors] = useState<{ discount?: string; dates?: string }>({});
  const minStart = localNow.toISOString().slice(0, 16);

  // Validate that end date is at least 1 hour after start
  const minEndDate = new Date(startDate);
  minEndDate.setHours(minEndDate.getHours() + 1);
  const minEnd = minEndDate.toISOString().slice(0, 16);

  if (!isOpen) return null;

  // ✅ Validate discount
  const validateDiscount = (value: number, type: 'percentage' | 'fixed'): string | null => {
    if (type === 'percentage') {
      if (value < 0) return 'Percentage cannot be negative';
      if (value > 100) return 'Percentage cannot exceed 100%';
      if (value === 0) return 'Discount must be greater than 0';
      return null;
    } else {
      if (value < 0) return 'Amount cannot be negative';
      if (value > basePrice) return `Amount cannot exceed ${formatPrice(basePrice)}`;
      if (value === 0) return 'Discount must be greater than 0';
      return null;
    }
  };

  // Validate dates
  const validateDates = (start: string, end: string): string | null => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const now = new Date();

    // 5-minute buffer
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    if (startDate < fiveMinutesAgo) {
      return 'Start date is too far in the past. Please refresh and try again.';
    }

    if (endDate <= startDate) return 'End date must be after start date';

    // Minimum 1 hour sale period
    const minEndDate = new Date(startDate);
    minEndDate.setHours(minEndDate.getHours() + 1);
    if (endDate < minEndDate) return 'Sale must be at least 1 hour long';

    return null;
  };

  const handleDiscountChange = (value: number) => {
    setDiscountValue(value);
    const error = validateDiscount(value, discountType);
    setErrors(prev => ({ ...prev, discount: error || undefined }));
  };

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    const error = validateDates(start, end);
    setErrors(prev => ({ ...prev, dates: error || undefined }));
  };

  // ✅ Clear errors when switching discount type
  const handleDiscountTypeChange = (type: 'percentage' | 'fixed') => {
    setDiscountType(type);
    setErrors(prev => ({ ...prev, discount: undefined }));
    // Re-validate current value with new type
    const error = validateDiscount(discountValue, type);
    if (error) {
      setErrors(prev => ({ ...prev, discount: error }));
    }
  };

  const handleConfirm = () => {
    // Validate all fields
    const discountError = validateDiscount(discountValue, discountType);
    const datesError = validateDates(startDate, endDate);

    if (discountError || datesError) {
      setErrors({
        discount: discountError || undefined,
        dates: datesError || undefined
      });
      return;
    }

    const calculatedDiscount = discountType === 'percentage'
      ? discountValue
      : Math.round((discountValue / basePrice) * 100);

    onConfirm({
      discount: calculatedDiscount,
      discountType,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    });
  };

  const calculatedDiscount = discountType === 'percentage'
    ? discountValue
    : Math.round((discountValue / basePrice) * 100);

  const salePrice = discountType === 'percentage'
    ? basePrice * (1 - discountValue / 100)
    : basePrice - discountValue;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Start Sale</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Product Info */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            {productImage && (
              <img
                src={productImage}
                alt={productName}
                className="w-16 h-16 object-cover rounded-lg"
              />
            )}
            <div>
              <p className="font-medium text-gray-900">{productName}</p>
              <p className="text-sm text-gray-500">
                Base Price: {formatPrice(basePrice)}
              </p>
            </div>
          </div>

          {/* Context Info */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <p>
              This sale will apply to {isVariant ? `"${productName}" variant only` : 'all variants of this product'}
            </p>
          </div>

          {/* Discount Type Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => handleDiscountTypeChange('percentage')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${discountType === 'percentage'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <Percent size={14} className="inline mr-1" />
              Percentage
            </button>
            <button
              type="button"
              onClick={() => handleDiscountTypeChange('fixed')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${discountType === 'fixed'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <DollarSign size={14} className="inline mr-1" />
              Fixed Amount
            </button>
          </div>

          {/* Discount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
            </label>
            {discountType === 'percentage' ? (
              <div className="relative">
                <input
                  type="number"
                  value={discountValue}
                  onChange={(e) => handleDiscountChange(Number(e.target.value))}
                  min={0}
                  max={100}
                  step={1}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-16 ${errors.discount ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  %
                </span>
              </div>
            ) : (
              <PriceInput
                value={discountValue}
                onChange={handleDiscountChange}
                placeholder="Enter amount"
                className={`w-full ${errors.discount ? 'border-red-500 bg-red-50' : ''}`}
                min={0}
                max={basePrice}
              />
            )}
            {errors.discount && (
              <p className="mt-1 text-sm text-red-600">{errors.discount}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {discountType === 'percentage'
                ? `Sale price: ${formatPrice(salePrice)} (${discountValue ?? 0}% off)`
                : `Discount: ${formatPrice(discountValue)} → Sale price: ${formatPrice(salePrice)}`
              }
            </p>
          </div>

          {/* Date Range */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  min={minStart}
                  onChange={(e) => handleDateChange(e.target.value, endDate)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dates ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  min={minEnd}
                  onChange={(e) => handleDateChange(startDate, e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dates ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                />
              </div>
            </div>
            {errors.dates && (
              <p className="mt-1 text-sm text-red-600">{errors.dates}</p>
            )}
          </div>

          {/* Summary */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Sale Price:</span> {formatPrice(salePrice)}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Discount:</span> {calculatedDiscount}% off
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Duration:</span>{' '}
              {formatDateTimeEnglish(startDate)} → {formatDateTimeEnglish(endDate)}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Sale
          </button>
        </div>
      </div>
    </div>
  );
};